// Stateful hook for the Manufacturing → Plants right-pane dealer
// roster. Replaces the old useLogisticsDealersPanel — same underlying
// API surface (mfg_dealer_org_settings + mfg_dealer_locations) but
// scoped to a selected plant + adds an `attach` action so the right
// pane can re-point an existing dealer at the active plant from a
// picker.
//
// State:
//   - subscriberDealers + dealerLocations + plants (full lists; the
//     hook handles plant-scoped slicing via plantDealersFilter helpers)
//   - rosterCounts (per-plant attached count + unattached bucket size)
//   - per-row edit drafts for the non-app locations section
//   - location create draft (right-pane "+ Add non-app location")
//   - attach picker draft state (the +Attach existing dealer modal)
//
// Subscriber-row edit (set primary_plant_id) is gone from this hook —
// the only operations on subscriber dealers in the new Plants-side
// shape are **Detach** (set primary_plant_id = null) and **Attach via
// picker**. Editing a subscriber row's primary plant directly doesn't
// make sense when the row is filtered to the selected plant (you'd be
// editing it OUT of view).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listManufacturingDealers,
  setManufacturingDealerPrimaryPlant,
  setManufacturingDealerServiceResale,
  updateManufacturingDealerLocation,
} from "@/lib/manufacturing/dealersClient";
import { listManufacturingPlants, type MfgPlant } from "@/lib/manufacturingClient";
import type {
  MfgDealerLocation,
  MfgSubscriberDealer,
} from "@/lib/manufacturing/dealerTypes";
import { isRealPlantSelection, isUnattachedSelection, type PlantSelection } from "./plantDealersDrafts";
import { usePlantDealersLocationActions } from "./usePlantDealersLocationActions";
import {
  filterLocationsBySelection,
  filterSubscribersBySelection,
  locationsAttachableToPlant,
  subscribersAttachableToPlant,
  tallyDealerRosterCounts,
  type DealerRosterCounts,
} from "./plantDealersFilter";

export type AttachTarget =
  | { kind: "subscriber"; dealer_org_id: string }
  | { kind: "location"; location_id: string };

export function useManufacturingPlantDealersTab(
  refreshTick: number,
  selection: PlantSelection
) {
  const [subscriberDealers, setSubscriberDealers] = useState<MfgSubscriberDealer[]>([]);
  const [dealerLocations, setDealerLocations] = useState<MfgDealerLocation[]>([]);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [attachPickerOpen, setAttachPickerOpen] = useState(false);

  const reload = useCallback(async () => {
    setListBusy(true);
    const [d, p] = await Promise.all([listManufacturingDealers(), listManufacturingPlants()]);
    setListBusy(false);
    if (!d.ok) {
      setLocalError(d.error);
      return;
    }
    setSubscriberDealers(d.data.subscriber_dealers);
    setDealerLocations(d.data.dealer_locations);
    if (p.ok) setPlants(p.data.plants);
  }, []);

  const locationActions = usePlantDealersLocationActions(
    selection,
    reload,
    setActionBusy,
    setLocalError
  );

  useEffect(() => {
    void reload();
  }, [reload, refreshTick]);

  const plantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of plants) m.set(p.id, `${p.plant_code} · ${p.name}`);
    return m;
  }, [plants]);

  const rosterCounts: DealerRosterCounts = useMemo(
    () => tallyDealerRosterCounts(subscriberDealers, dealerLocations),
    [subscriberDealers, dealerLocations]
  );

  const scopedSubscribers = useMemo(
    () => filterSubscribersBySelection(subscriberDealers, selection),
    [subscriberDealers, selection]
  );

  const scopedLocations = useMemo(
    () => filterLocationsBySelection(dealerLocations, selection),
    [dealerLocations, selection]
  );

  const attachableSubscribers = useMemo(() => {
    if (!isRealPlantSelection(selection)) return [];
    return subscribersAttachableToPlant(subscriberDealers, selection as string);
  }, [subscriberDealers, selection]);

  const attachableLocations = useMemo(() => {
    if (!isRealPlantSelection(selection)) return [];
    return locationsAttachableToPlant(dealerLocations, selection as string);
  }, [dealerLocations, selection]);

  // --- Subscriber detach (only operation here; attach happens via picker) ---

  const detachSubscriber = useCallback(
    async (sub: MfgSubscriberDealer) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await setManufacturingDealerPrimaryPlant({
        dealer_org_id: sub.dealer_org_id,
        primary_plant_id: null,
        notes: sub.notes,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  // Slice 8: per-row "Set primary plant" affordance for the
  // Unattached subscriber list. Same wire-up as detachSubscriber but
  // accepts a target plant id instead of null. Distinct from the
  // attach-via-picker flow (attachToActivePlant) — that one assumes a
  // plant is currently selected and the picker chooses the dealer;
  // here the dealer is fixed (the row) and the user picks the plant
  // from an inline dropdown.
  const assignSubscriberToPlant = useCallback(
    async (sub: MfgSubscriberDealer, plantId: string) => {
      if (!plantId) return;
      setActionBusy(true);
      setLocalError(null);
      const res = await setManufacturingDealerPrimaryPlant({
        dealer_org_id: sub.dealer_org_id,
        primary_plant_id: plantId,
        notes: sub.notes,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const setSubscriberServiceResale = useCallback(
    async (sub: MfgSubscriberDealer, enabled: boolean) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await setManufacturingDealerServiceResale({
        subscriber_org_id: sub.dealer_org_id,
        service_resale_enabled: enabled,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      setSubscriberDealers((prev) =>
        prev.map((row) =>
          row.dealer_org_id === sub.dealer_org_id
            ? {
                ...row,
                service_resale_enabled: res.data.settings.service_resale_enabled,
                service_resale_settings_id: res.data.settings.id,
              }
            : row
        )
      );
    },
    []
  );

  // --- Attach picker actions ---

  const openAttachPicker = useCallback(() => {
    if (!isRealPlantSelection(selection)) return;
    setAttachPickerOpen(true);
    setLocalError(null);
  }, [selection]);

  const closeAttachPicker = useCallback(() => {
    setAttachPickerOpen(false);
  }, []);

  const attachToActivePlant = useCallback(
    async (target: AttachTarget) => {
      if (!isRealPlantSelection(selection)) return;
      const plantId = selection as string;
      setActionBusy(true);
      setLocalError(null);
      const res =
        target.kind === "subscriber"
          ? await setManufacturingDealerPrimaryPlant({
              dealer_org_id: target.dealer_org_id,
              primary_plant_id: plantId,
              // Preserve existing notes if any; the endpoint upserts on
              // (organization_id, dealer_org_id) so the row keeps its
              // notes column when we pass through the current value.
              notes:
                subscriberDealers.find((s) => s.dealer_org_id === target.dealer_org_id)?.notes ??
                "",
            })
          : await updateManufacturingDealerLocation({
              location_id: target.location_id,
              primary_plant_id: plantId,
            });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      setAttachPickerOpen(false);
      await reload();
    },
    [selection, subscriberDealers, reload]
  );

  return {
    subscriberDealers,
    dealerLocations,
    plants,
    plantNameById,
    listBusy,
    actionBusy,
    localError,

    selection,
    isUnattached: isUnattachedSelection(selection),
    isRealPlant: isRealPlantSelection(selection),
    rosterCounts,

    scopedSubscribers,
    scopedLocations,

    detachSubscriber,
    assignSubscriberToPlant,
    setSubscriberServiceResale,

    ...locationActions,

    attachPickerOpen,
    attachableSubscribers,
    attachableLocations,
    openAttachPicker,
    closeAttachPicker,
    attachToActivePlant,

    reload,
  };
}

export type UseManufacturingPlantDealersTab = ReturnType<typeof useManufacturingPlantDealersTab>;
